import { useEffect, useState, useCallback } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getProposals, createProposal, updateProposalStatus, deleteProposal,
  getMeetings,
} from '../services/ApiService';
import type { Proposal, Meeting } from '../types/models';
import Modal from '../components/Modal';
import { Plus, Lightbulb, Check, X as XIcon, Clock, Trash2 } from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

export default function ProposalsPage() {
  const { currentOrg, isBoardMember } = useOrganization();
  const { user } = useAuth();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', meetingId: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const [p, m] = await Promise.all([
        getProposals(currentOrg.id),
        getMeetings(currentOrg.id),
      ]);
      setProposals(p);
      setMeetings(m);
    } catch {
      setProposals([]);
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    setSaving(true);
    try {
      await createProposal(currentOrg.id, {
        title: form.title,
        content: form.content,
        linkedMeetingId: form.meetingId || null,
      });
      track(AnalyticsEvents.ProposalCreated, { orgId: currentOrg.id, linkedToMeeting: !!form.meetingId });
      setShowModal(false);
      setForm({ title: '', content: '', meetingId: '' });
      fetchData();
    } catch {
      alert('Kunde inte skicka förslaget.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
    if (!currentOrg) return;
    try {
      await updateProposalStatus(currentOrg.id, id, status);
      fetchData();
    } catch {
      alert('Kunde inte uppdatera status.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!currentOrg) return;
    if (!window.confirm('Ta bort detta förslag?')) return;
    try {
      await deleteProposal(currentOrg.id, id);
      fetchData();
    } catch {
      alert('Kunde inte ta bort förslaget.');
    }
  };

  const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
    pending: { label: 'Under behandling', color: 'bg-amber-50 text-amber-700', icon: Clock },
    approved: { label: 'Godkänt', color: 'bg-green-50 text-green-700', icon: Check },
    rejected: { label: 'Avslaget', color: 'bg-red-50 text-red-700', icon: XIcon },
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return <div className="p-8 text-center text-gray-500">Laddar...</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Förslag</h1>
          <p className="text-gray-500 text-sm mt-1">Lämna och granska förslag</p>
        </div>
        <button data-tour="proposal-add" onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm">
          <Plus className="w-4 h-4" />
          Nytt förslag
        </button>
      </div>

      {proposals.length === 0 ? (
        <div className="text-center py-16">
          <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Inga förslag inlämnade</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map(p => {
            const cfg = statusConfig[p.status] ?? statusConfig.pending;
            const StatusIcon = cfg.icon;
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{p.title}</h3>
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{p.submittedBy.fullName}</span>
                      <span className="text-gray-300">|</span>
                      <span>{formatDate(p.createdAt)}</span>
                      {p.linkedMeetingTitle && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-teal-600">{p.linkedMeetingTitle}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isBoardMember && (
                    <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg hover:bg-red-50">
                      <Trash2 className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
                <p className="text-gray-700 text-sm whitespace-pre-wrap mt-3 leading-relaxed">{p.content}</p>
                {isBoardMember && p.status === 'pending' && (
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleUpdateStatus(p.id, 'approved')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Godkänn
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(p.id, 'rejected')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100"
                    >
                      <XIcon className="w-3.5 h-3.5" />
                      Avslå
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nytt förslag">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Titel</label>
            <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Kopplat möte (valfritt)</label>
            <select value={form.meetingId} onChange={e => setForm({ ...form, meetingId: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none">
              <option value="">Inget möte</option>
              {meetings.filter(m => new Date(m.meetingDate) >= new Date()).map(m => (
                <option key={m.id} value={m.id}>{m.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Beskrivning</label>
            <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} required rows={6} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none" />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving ? 'Skickar...' : 'Skicka förslag'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
